import { FC, FocusEventHandler, memo, useCallback, useMemo } from 'react';
import { useController } from 'react-hook-form';

import FormControl from '@material-ui/core/FormControl';
import { Skeleton } from '@material-ui/lab';

import { HelperTxt } from '../helperTxt/HelperTxt';
import { Input } from '../input/Input';
import { FormInputProps } from './FormItems.types';

export const FormInput: FC<FormInputProps> = memo(
  ({
    control,
    className,
    disabled,
    error: errorProp,
    name,
    required,
    requiredText,
    size = 'medium',
    variant = 'outlined',
    loading = false,
    onBlur,
    type,
    ...rest
  }) => {
    const {
      field,
      fieldState: { error },
    } = useController({
      name,
      control,
      defaultValue: undefined,
    });

    const helperText = useMemo(() => {
      if (error) {
        return error.message;
      }

      if (required) {
        return requiredText || '*required';
      }

      return '';
    }, [error, required, requiredText]);

    const handleBlur: FocusEventHandler<HTMLInputElement> = useCallback(
      e => {
        if (onBlur) {
          onBlur(e);
        }

        field.onBlur();
      },
      [field.onBlur, onBlur],
    );

    // Hidden input
    if (type === 'hidden') {
      return <input {...rest} {...field} type={type} disabled={disabled} required={required} />;
    }

    // Show skeleton when loading
    if (loading) {
      let skeletonHeight = 55;
      if (size === 'small') {
        skeletonHeight = 46;
      } else if (size === 'large') {
        skeletonHeight = 61;
      }

      return (
        <FormControl fullWidth className={className}>
          <Skeleton variant="rect" height={skeletonHeight} />
          {required && <Skeleton variant="text" width={80} height={24} style={{ marginTop: 4 }} />}
        </FormControl>
      );
    }

    return (
      <FormControl error={!!error} fullWidth className={className}>
        <Input
          {...rest}
          {...field}
          disabled={disabled}
          error={!!error || errorProp}
          required={required}
          size={size}
          variant={variant}
          onBlur={handleBlur}
          type={type}
          aria-label={rest.label || name}
          {...(error && { 'aria-invalid': true })}
        />

        {helperText && <HelperTxt>{helperText}</HelperTxt>}
      </FormControl>
    );
  },
);
